/**
 * Shared styles.
 *
 * Inline style objects rather than a CSS-in-JS library: the whole app is a
 * handful of screens, and spec §7 budgets under 150KB of JS on first load. A
 * styling runtime would spend a chunk of that budget on something a plain
 * object already does.
 *
 * `satisfies` rather than an annotation keeps the literal keys, so a typo in
 * `S.card` is a compile error instead of an undefined style that silently
 * renders unstyled.
 */

export const BUTTON_BASE = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  background: 'var(--surface)',
  color: 'var(--text)',
  padding: '12px 14px',
  fontSize: 16,
  // 44px is the smallest reliably tappable target on a cheap phone.
  minHeight: 44,
  cursor: 'pointer',
} as const satisfies React.CSSProperties;

export const S = {
  page: { padding: 16, maxWidth: 560, margin: '0 auto', paddingBottom: 88 },
  h1: { fontSize: 20, marginTop: 0 },
  h2: { fontSize: 14, color: 'var(--muted)', fontWeight: 600, margin: '0 0 8px' },
  muted: { color: 'var(--muted)', fontSize: 13 },
  error: { color: '#fca5a5', fontSize: 14 },
  success: { color: 'var(--win)', fontSize: 14 },

  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 14,
  },

  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    fontSize: 16, // 16px stops iOS zooming the viewport on focus.
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--bg)',
    color: 'var(--text)',
    minHeight: 48,
  },

  label: { display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 6 },
  field: { marginBottom: 14 },

  button: BUTTON_BASE,
  buttonPrimary: {
    ...BUTTON_BASE,
    width: '100%',
    background: 'var(--win)',
    color: '#06281c',
    borderColor: 'var(--win)',
    fontWeight: 800,
    fontSize: 17,
    minHeight: 52,
  },
  buttonDisabled: {
    ...BUTTON_BASE,
    width: '100%',
    opacity: 0.5,
    cursor: 'not-allowed',
    minHeight: 52,
  },
  buttonGhost: { ...BUTTON_BASE, background: 'none', border: 'none', color: 'var(--muted)' },

  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },

  // Bottom tab bar. Thumb-reachable on a big cheap phone; a top nav is not.
  tabs: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    background: 'var(--surface)',
    borderTop: '1px solid var(--border)',
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  tab: {
    background: 'none',
    border: 'none',
    color: 'var(--muted)',
    padding: '10px 4px',
    fontSize: 12,
    minHeight: 52,
    cursor: 'pointer',
  },
  tabActive: {
    background: 'none',
    border: 'none',
    borderTop: '2px solid var(--accent)',
    color: 'var(--accent)',
    padding: '10px 4px',
    fontSize: 12,
    minHeight: 52,
    fontWeight: 700,
    cursor: 'pointer',
  },
} as const satisfies Record<string, React.CSSProperties>;
